import styles from "./intro.module.scss";
import { BsArrowLeftShort, BsArrowRightCircleFill, BsArrowRightShort, BsCheckCircleFill } from "react-icons/bs";
import { ActionCall, Button, Alert } from "cdhs-ui-library";
import { getQuestion, submitSurvey } from "../../store/survey/surveySlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { useNavigate } from "react-router";
import { ROUTES } from '../../config/routes';
import { useEffect, useState } from "react";
import IMAGE from "../../config/imgConfig";
import { useTranslations } from "../../contexts/LanguageContext";
import { QuestionResponse } from "../../types";
import ContactUsButton from "../../components/ButtonContactUs/ButtonContactUs";
interface IntroProps {
  incentive: string | undefined;
}
function Intro({ incentive }: IntroProps) {
  const t = useTranslations();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isSurveyDeclined, setIsSurveyDeclined] = useState<boolean>(false);
  const [surveyCompletedAlert, setSurveyCompletedAlert] = useState<boolean>(false)
  const language = useAppSelector((state) => state.lang);
  const user = useAppSelector((state) => state.user.user);
  const isDashboardUser = useAppSelector((state) => state.user.isDashboardLaunch)
  const surveyType = useAppSelector((state) => state.user.surveyType)
  const survey = useAppSelector((state) => state.survey)
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.SurveyStatus == "C") {
      setSurveyCompletedAlert(true)
    } else {
      return
    }
  }, [user?.SurveyStatus])

  const handleSurveyStatusAlert = () => {
    setSurveyCompletedAlert(prevState => !prevState)
    navigate(ROUTES.USER_PROFILE)
  }

  type GetQuestionFulfilledAction = {
    type: string;
    payload: QuestionResponse;
    meta: { requestStatus: 'fulfilled';[key: string]: any };
    error?: undefined;
  };
  async function initialAPICall() {
    try {
      const dispatchResult = dispatch(getQuestion({ //return redux object
        SessionId: user?.SessionId,
        ClientId: user?.ClientId,
        Language: language.language,
        QuestionId: 0,
        SubmitType: "Next"
      }));
      const result = await dispatchResult as GetQuestionFulfilledAction;

      if (result.meta.requestStatus === 'fulfilled') {
        const responseData = result.payload;

        if (responseData?.ResponseCode === "SURVEY-4007") {
          navigate(ROUTES.SURVEY_CONFIRM);
        } else {
          navigate(ROUTES.SURVEY_QUESTIONS);
        }
      } else {
        console.error("API call failed:", result.payload);
        navigate(ROUTES.USER_PROFILE);
      }
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      return error;
    }
  }

  async function declineSurvey() {
    try {
      const result = await dispatch(submitSurvey({ SessionId: user?.SessionId, ClientId: user?.ClientId, Decline: true }));
      if (result) {
        setIsSurveyDeclined(true);
      }
    } catch (error) {
      console.error("FAILED TO FULFILL DECLINE SURVEY: ", error);
    }
  }

  const handleDeclineModal = () => {
    setOpenModal(prevState => !prevState);
  };

  if (isSurveyDeclined || survey.allAnswersDeclined) {
    return (
      <section aria-labelledby="survey-declined-heading" className={styles.overlay}>
        <div className={styles.mainContainer}>
          <div className={styles.header} aria-hidden="true">
            <BsCheckCircleFill />
          </div>
          <div className={styles.content}>
            <h2 id="survey-declined-heading">{t('surveyDeclined_title', 'Survey declined.')}</h2>
            <p>{t('surveyDeclined_text', 'If you change your mind or would like to provide feedback later, please contact your caseworker.')}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="survey-intro-heading" className={styles.overlay}>
      <div className={styles.mainContainer}>
        <div className={styles.header} aria-hidden="true">
          <BsArrowRightCircleFill />
        </div>
        <div className={styles.content}>
          <h1 id="survey-intro-heading">
            {t('introPage_greeting', '{firstName}, your story is important!').replace('{firstName}', user?.FirstName || 'Friend')}
          </h1>
          <p>{t('introPage_paragraph1', "By sharing your experiences in this survey, you'll help us understand how we can better serve young people like you.")}</p>
          <p>
            {t('introPage_incentiveInfoPart1', "After you complete and submit the survey, you'll get instructions on how to receive your ")}
            {incentive}
            {t('introPage_incentiveInfoPart2', " incentive.")}
          </p>
          <p>{t('introPage_resourcesInfo', "We'll also provide a list of resources that may be helpful to you.")}</p>

          <Button
            caption={t('introPage_startButton', 'Start the Survey')}
            variant="NYTD"
            size="large"
            fullWidth={true}
            iconPosition="Right"
            image={<BsArrowRightShort />}
            onClick={initialAPICall}
          />
          {(isDashboardUser && surveyType === "17") && <ActionCall
            label={t('introPage_declineButtonLabel', 'Decline Survey')}
            onClick={() => handleDeclineModal()}
          />}
          <Alert
            open={openModal}
            onClose={() => handleDeclineModal()}
            type="caution"
          >
            <div className={styles['modal']}>
              <h2 id="decline-modal-title-id">{t('declineModal_title', 'Your voice matters!')}</h2>
              <img
                src={IMAGE.DECLINE_MODAL.url}
                alt={t('alt_declineModal', IMAGE.DECLINE_MODAL.alt)}
              />
              <p>{t('declineModal_text', 'Just a few minutes of your time can help improve services for youth in your community. Please consider taking the survey')}</p>
              <Button
                caption={t('declineModal_takeSurveyButton', 'Take the survey')}
                variant="NYTD"
                mode="light"
                size="medium"
                fullWidth={true}
                iconPosition="Left"
                image={<BsArrowLeftShort />}
                onClick={() => {
                  initialAPICall();
                  handleDeclineModal();
                }}
              />
              <Button
                caption={t('declineModal_declineSurveyButton', 'Decline the survey')}
                variant="NYTD"
                size="medium"
                fullWidth={true}
                onClick={() => {
                  declineSurvey();
                  handleDeclineModal();
                }}
              />
            </div>
          </Alert>
          <Alert
            open={surveyCompletedAlert}
            onClose={() => handleSurveyStatusAlert()}
            type="caution"
          >
            <div className={styles['modal']}>
              <h2 id="completed-survey-title-id">{t('completedSurvey_title', 'You’ve already completed this survey.')}</h2>
              <p>{t('completedSurvey_text', 'Our records indicate that you have already completed and submitted this survey. Please contact us if you think this is a mistake.')}</p>
              <ContactUsButton onClick={() => {
                handleSurveyStatusAlert();
              }}
               option="caution" />
              <ActionCall label="Close" onClick={() => { setSurveyCompletedAlert(prevState => !prevState); navigate(ROUTES.USER_PROFILE) }}>

              </ActionCall>
            </div>
          </Alert>
        </div>
      </div>
    </section>
  );
}

export default Intro;